import React, { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { makeStyles, Typography } from '~/lib/bn-material-ui';
import { BinProfileIngredient } from '~/bins/bin-level/bin-profile-ingredient';
import { entityStore, sirenActions, useBinLevelMeasurementOverrides, useMeasurement } from '~/utility/BinSentry-ui-utility';
import useSirenEntity from '~/utility/use-siren-entity';
import { BARN_ASSUMED_BULK_DENSITY } from '~/organizations/barns/barn-page/insights-graph/barn-insights-helpers';
import { useAsyncFetch } from '~/common/use-async-fetch';
import parseSiren from 'siren-parser';
import { getSiren } from '~/common/get-siren';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  levelBar: {
    height: '18px',
    width: '80px',
  },
  date: {
    fontSize: '0.8rem',
    color: theme.palette.gray[70],
  },
  level: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export function BinVolume({ binEntity, volume }) {
  const bulkDensity = binEntity?.properties.bulkDensity || BARN_ASSUMED_BULK_DENSITY;
  const measurementOverrides = useBinLevelMeasurementOverrides({ bin: binEntity });
  const measurement = useMeasurement({ name: 'binLevel', ...measurementOverrides });
  const value = measurement.convert({ volume, bulkDensity });
  return (
    <Typography component='span'>
      {isNaN(value) ? '-' : Math.round(value)} {measurement.unit}
    </Typography>
  );
}

export function BinLevel({ binEntity, includeDate = false }) {
  const styles = useStyles();
  const { entity: levelEntity } = useLatestLevelFromPageNow({ binHref: binEntity?.getLinkByRel('self').href });
  const latestLevel = levelEntity?.properties?.volume;
  const { publishedAt } = levelEntity?.properties || {};
  return (
    <div className={styles.level}>
      <Typography component='span'>
        <BinVolume binEntity={binEntity} volume={latestLevel} />
      </Typography>
      {includeDate && (
        <Typography component='span' className={styles.date}>
          {publishedAt ? DateTime.fromISO(publishedAt).toFormat('MMM d, h:mm a') : '-'}
        </Typography>
      )}
    </div>
  );
}

export function BinLevelMeter({ binEntity }) {
  const styles = useStyles();
  return (
    <div className={styles.container}>
      <div className={styles.levelBar}>
        <BinProfileIngredient href={binEntity?.getLink('self').href} horizontal />
      </div>
    </div>
  );
}

export function BinCapacity({ binEntity }) {
  const capacity = binEntity?.properties.volumes.fullTotal;
  return <BinVolume binEntity={binEntity} volume={capacity} />;
}

export function BinAvailableCapacity({ binEntity }) {
  const fullTotal = binEntity?.properties.volumes.fullTotal;
  const { entity: levelEntity } = useLatestLevelFromPageNow({ binHref: binEntity?.getLinkByRel('self').href });
  const latestLevel = levelEntity?.properties?.volume;
  if (fullTotal === undefined || latestLevel === undefined) {
    return <Typography component='span'>-</Typography>;
  }
  const capacity = Math.max(0, fullTotal - latestLevel);
  return <BinVolume binEntity={binEntity} volume={capacity} />;
}

export function BinMaterial({ binEntity }) {
  const { entity: tagsEntity } = useSirenEntity({ href: binEntity?.getSubEntityByRel('https://api.binsentry.com/rel/tags')?.href });
  const tags = tagsEntity?.entities.filter(tag => tag.class.includes('tag')) || [];
  const material = tags.find(tag => tag.properties.key === '_material')?.properties.value;

  return (
    <Typography component='span'>
      {material || '-'}
    </Typography>
  );
}

export function BinBulkDensity({ binEntity }) {
  const measurement = useMeasurement({ name: 'bulkDensity' });
  const bulkDensity = binEntity?.properties.bulkDensity || BARN_ASSUMED_BULK_DENSITY;
  const value = measurement.convert({ bulkDensity });
  return (
    <Typography component='span'>
      {isNaN(value) ? '-' : value} {measurement.unit}
    </Typography>
  );
}

export function useLatestLevelFromPageNow({ binHref }) {
  const { entity: binEntity, loading: loadingBin } = useSirenEntity({ href: binHref });
  const { result: latestBinLevelEntity, loading: loadingLevel } = useAsyncFetch(async () => {
    const binLevelEntity = await getLatestBinLevelEntityFromPageNow({ binEntity });
    return binLevelEntity;
  }, [binEntity, binHref]);

  return {
    entity: latestBinLevelEntity,
    loading: loadingBin || loadingLevel,
  };
}

export async function getLatestBinLevelEntityFromPageNow ({ binEntity } = {}) {
  if (!binEntity) return undefined;
  const binLevelsHref = binEntity.getSubEntity('https://api.binsentry.com/rel/v2/bin-levels')?.href;
  if (!binLevelsHref) return undefined;
  const binLevelsEntity = await getSiren(binLevelsHref);
  const action = binLevelsEntity?.getAction('get-latest-bin-level-before-date');
  if (!action) return undefined;
  const { entity: latestBinLevel } = await sirenActions.submitAction(
    action,
    { beforeDate: DateTime.utc().toISO() },
    { bypassCache: false },
  );
  return latestBinLevel;
}
